/** MongoDB ObjectId (24 hex). */
export const MONGODB_OBJECT_ID_RE = /^[a-f0-9]{24}$/i;

const ITEM_SLUG_ID_SUFFIX_RE = /-([a-f0-9]{24})$/i;

/**
 * @param {string} text
 * @returns {string}
 */
export function slugifyDynamicItemTitle(text) {
  return (
    String(text || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 80) || "item"
  );
}

/**
 * @param {string} modelName
 * @param {string} itemId
 * @param {string} [title]
 * @returns {string}
 */
export function buildDynamicItemPrettySlug(modelName, itemId, title) {
  const mn = String(modelName || "").trim();
  const id = String(itemId || "").trim();
  const slug = slugifyDynamicItemTitle(title || mn || "item");
  return `${mn}-${slug}-${id}`;
}

/**
 * @param {string} itemSlug
 * @param {string[]} [modelNames]
 * @returns {{ modelName: string, itemId: string, titleSlug: string } | null}
 */
export function parseDynamicItemPrettySlug(itemSlug, modelNames = []) {
  let raw = String(itemSlug || "").trim();
  if (!raw) return null;
  try {
    raw = decodeURIComponent(raw);
  } catch {
    /* keep raw */
  }

  const match = raw.match(ITEM_SLUG_ID_SUFFIX_RE);
  if (!match) return null;
  const itemId = match[1];
  const prefix = raw.slice(0, -(itemId.length + 1));
  if (!prefix) return null;

  const names = [...modelNames]
    .filter(Boolean)
    .sort((a, b) => b.length - a.length);

  for (const name of names) {
    const prefixLower = prefix.toLowerCase();
    const nameLower = String(name).toLowerCase();
    if (prefixLower === nameLower) {
      return { modelName: name, itemId, titleSlug: "" };
    }
    if (prefixLower.startsWith(`${nameLower}-`)) {
      return {
        modelName: name,
        itemId,
        titleSlug: prefix.slice(name.length + 1),
      };
    }
  }

  const firstHyphen = prefix.indexOf("-");
  if (firstHyphen === -1) {
    return { modelName: prefix, itemId, titleSlug: "" };
  }
  return {
    modelName: prefix.slice(0, firstHyphen),
    itemId,
    titleSlug: prefix.slice(firstHyphen + 1),
  };
}

/**
 * @param {string} modelName
 * @param {string} itemId
 * @param {string} [title]
 * @param {{ pretty?: boolean }} [options]
 * @returns {string}
 */
export function buildDynamicModelItemViewPath(
  modelName,
  itemId,
  title,
  options = {}
) {
  const mn = String(modelName || "").trim();
  const id = String(itemId || "").trim();
  if (!mn || !id) return "";

  const usePretty =
    options.pretty !== false && MONGODB_OBJECT_ID_RE.test(id);

  if (usePretty) {
    return `/i/${buildDynamicItemPrettySlug(mn, id, title)}`;
  }

  return `/dynamic-model/${encodeURIComponent(mn)}/view/${encodeURIComponent(id)}`;
}

/**
 * @param {import('vue-router').Route} route
 * @param {string[]} [modelNames]
 * @returns {{ modelName: string, itemId: string }}
 */
export function resolveDynamicModelRouteParams(route, modelNames = []) {
  if (!route || !route.params) {
    return { modelName: "", itemId: "" };
  }

  const directModel = route.params.modelName;
  const directId = route.params.itemId;
  if (directModel && directId) {
    return {
      modelName: String(directModel).trim(),
      itemId: String(directId).trim(),
    };
  }

  if (route.params.itemSlug) {
    const parsed = parseDynamicItemPrettySlug(route.params.itemSlug, modelNames);
    if (parsed) {
      return { modelName: parsed.modelName, itemId: parsed.itemId };
    }
  }

  return { modelName: "", itemId: "" };
}

/**
 * Tras cargar la ficha, sustituye la URL legacy por la legible (/i/…).
 * @param {import('vue-router').default} router
 * @param {import('vue-router').Route} route
 * @param {string} modelName
 * @param {{ _id?: string, name?: string, title?: string } | null | undefined} item
 */
export function maybeReplaceWithPrettyItemViewUrl(router, route, modelName, item) {
  if (!router || !route || route.name !== "DynamicModelDetail") return;
  const itemId = item && item._id ? String(item._id) : "";
  const title =
    item && (item.name || item.title)
      ? String(item.name || item.title)
      : "";
  if (!itemId || !modelName) return;

  const pretty = buildDynamicModelItemViewPath(modelName, itemId, title, {
    pretty: true,
  });
  if (!pretty) return;

  const nextQuery =
    route.query && typeof route.query === "object" ? { ...route.query } : {};
  const nextLocation = { path: pretty, query: nextQuery };
  const nextFull = router.resolve(nextLocation).route.fullPath;
  if (route.fullPath !== nextFull) {
    router.replace(nextLocation).catch(() => {});
  }
}
